import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { FontSize, Spacing, BorderRadius } from '../theme/colors';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface AccessDeniedScreenProps {
  emoji?: string;
  title?: string;
  subtitle?: string;
  hint?: string;
  /** Signs out, so someone who used the wrong email can sign in with the invited one. */
  onSignOut?: () => Promise<void> | void;
}

export function AccessDeniedScreen({
  emoji = '🚫🙈',
  title = 'Access Denied',
  subtitle = "You don't have permission to access this application.",
  hint = 'Please contact your school administrator if you believe this is a mistake.',
  onSignOut,
}: AccessDeniedScreenProps) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [signingOut, setSigningOut] = useState(false);

  const signOut = async () => {
    if (!onSignOut) return;
    setSigningOut(true);
    try {
      await onSignOut();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <View style={styles.container}>
      {/* Icon */}
      <Text style={styles.emoji}>{emoji}</Text>

      <Text style={styles.title}>{title}</Text>

      <Text style={styles.subtitle}>{subtitle}</Text>
      <Text style={styles.hint}>{hint}</Text>

      {onSignOut && (
        <TouchableOpacity
          style={[styles.signOutButton, signingOut && styles.signOutButtonBusy]}
          onPress={signOut}
          disabled={signingOut}
          accessibilityRole="button"
          activeOpacity={0.85}>
          <Text style={styles.signOutText}>
            {signingOut ? 'Signing out…' : 'Sign in with a different account'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
    padding: Spacing.xl,
  },
  emoji: {
    fontSize: Platform.OS === 'web' ? 100 : 72,
    marginBottom: Spacing.lg,
    textAlign: 'center',
  },
  title: {
    fontSize: Platform.OS === 'web' ? 48 : 32,
    fontWeight: '700',
    color: '#E74C3C',
    marginBottom: Spacing.md,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: FontSize.md,
    color: colors.textPrimary,
    marginBottom: Spacing.sm,
    textAlign: 'center',
    maxWidth: 400,
  },
  hint: {
    fontSize: FontSize.sm,
    color: colors.textMuted,
    marginBottom: Spacing.xl,
    textAlign: 'center',
    maxWidth: 400,
  },
  signOutButton: {
    backgroundColor: colors.primary,
    borderRadius: BorderRadius.md,
    paddingVertical: Spacing.sm + 4,
    paddingHorizontal: Spacing.lg,
  },
  signOutButtonBusy: {
    opacity: 0.6,
  },
  signOutText: {
    color: '#FFFFFF',
    fontSize: FontSize.md,
    fontWeight: '600',
    textAlign: 'center',
  },
});
// import React from 'react';
// import { View, Text, StyleSheet } from 'react-native';

// // Same messaging/layout as the staff web frontend's pages/accessDenied.tsx,
// // shown when /User/me reports hasWebAccess: false.
// export function AccessDeniedScreen() {
//   return (
//     <View style={styles.container}>
//       <Text style={styles.emoji}>🚫🙈</Text>
//       <Text style={styles.title}>Access Denied</Text>
//       <Text style={styles.message}>You don't have permission to view this page.</Text>
//       <Text style={styles.subMessage}>
//         Please contact your administrator if you believe this is a mistake.
//       </Text>
//     </View>
//   );
// }

// const styles = StyleSheet.create({
//   container: {
//     flex: 1,
//     justifyContent: 'center',
//     alignItems: 'center',
//     backgroundColor: '#EBEDEE',
//     padding: 20,
//   },
//   emoji: {
//     fontSize: 100,
//     marginBottom: 20,
//   },
//   title: {
//     fontSize: 40,
//     fontWeight: '800',
//     color: '#E74C3C',
//     marginBottom: 20,
//     textAlign: 'center',
//   },
//   message: {
//     fontSize: 18,
//     color: '#333333',
//     marginBottom: 10,
//     textAlign: 'center',
//   },
//   subMessage: {
//     fontSize: 14,
//     color: '#666666',
//     textAlign: 'center',
//   },
// });
